/**
 * Graph Feature Index
 * Barrel exports for graph visualization components.
 *
 * The four user-facing surfaces (`EcosystemGraph`, `EcosystemGraphPreview`,
 * `OrganizationSubgraph`, `PersonSubgraph`) and the mobile fallback
 * (`GraphMobileView`) are wrapped in `withCapability('networkGraph')` so
 * a tenant that has disabled the network-graph capability sees nothing on
 * any of these surfaces. The wrap lives here (not in the component files)
 * to keep the gate at the export boundary: call sites that import via
 * this barrel inherit it automatically, and the wrapper short-circuits
 * before the inner component's hooks / data fetches run.
 *
 * `GraphControls`, `GraphLegend`, and `GraphExplainer` are presentational
 * helpers consumed only inside the user-facing surfaces, so gating them
 * again would be redundant. The data-layer exports (`useGraphData`,
 * `useGraphFilters`, color helpers) are not user-facing and stay un-gated;
 * callers that want to programmatically read graph data still can (the
 * server `/api/graph/*` gate is the real boundary for tenant-disabled data
 * access).
 */
import withCapability from '@/shared/hooks/withCapability';

import EcosystemGraphRaw from './components/EcosystemGraph';
import EcosystemGraphPreviewRaw from './components/EcosystemGraphPreview';
import OrganizationSubgraphRaw from './components/OrganizationSubgraph';
import PersonSubgraphRaw from './components/PersonSubgraph';
import GraphMobileViewRaw from './components/GraphMobileView';

export const EcosystemGraph = withCapability(EcosystemGraphRaw, 'networkGraph');
export const EcosystemGraphPreview = withCapability(
    EcosystemGraphPreviewRaw,
    'networkGraph'
);
export const OrganizationSubgraph = withCapability(
    OrganizationSubgraphRaw,
    'networkGraph'
);
export const PersonSubgraph = withCapability(PersonSubgraphRaw, 'networkGraph');
export const GraphMobileView = withCapability(
    GraphMobileViewRaw,
    'networkGraph'
);

export { default as GraphControls } from './components/GraphControls';
export { default as GraphExplainer } from './components/GraphExplainer';
export { default as GraphLegend } from './components/GraphLegend';
export { useGraphData } from './hooks/useGraphData';
export { useGraphFilters } from './hooks/useGraphFilters';
export {
    SECTOR_COLORS,
    getNodeColor,
    buildRegionColors,
} from './utils/graphColors';
